// ─────────────────────────────────────────────────────────────────────────────
// Consensus aggregator — shared between OCR Lab (MatchCardDemo) and the live
// match card (MatchCardLive).
//
// Snaps OCR'd player names to the canonical roster (so "[m.o.m]d_rose",
// "[m.o.m]d.rose", "mjd_rose" all collapse to one row), buckets cell
// readings by (game, snapped player, field), weights votes by per-shot OCR
// confidence², and returns a per-game roster-ordered list with consensus
// values + agreement scores.
//
// Lifted out of MatchCardDemo on 2026-05-01 so the live match card can stop
// rendering raw flat OCR variants and show the same polished team-grouped
// scoreboard the lab has always had.
// ─────────────────────────────────────────────────────────────────────────────

import type { SubmissionSlotRow, ServerCellConsensusRow } from './api';

interface CellVote { value: number; weight: number; count: number }

export interface DropDiagnostic {
  raw: string;
  bestCandidate: string | null;
  score: number;
  gameNum: number;
}

export interface NameVote {
  raw: string;
  count: number;
}

export interface ConsensusCell {
  player: string;
  field: string;
  consensus?: number;
  agreement: number;
  reads: number;
  // Wave 6 (MatchCardLive only — aggregateConsensus's own output below never
  // sets these, so this stays undefined/absent for every existing caller):
  // 'server' marks a cell whose value/agreement/reads were overlaid from the
  // match's canonical ocrBatchSummary.consensus (see mergeServerConsensus
  // below) rather than recomputed here from raw slots. `flags` is a small
  // set of CellFlag-shaped labels (AGREED/MAJORITY/TIE/ALL_DIFFERENT/
  // NO_READINGS/STAGE2_CORRECTED) derived from that same server row's own
  // agreement/value/source — display sugar on canonical numbers, not a
  // second consensus computation.
  //
  // Lisa, 2026-07-13 — K/D strip precedence: 'strip' marks a cell whose
  // value was substituted by applyUploaderStripPrecedence (below) from the
  // player's OWN slot's uploaderStats — see that function's doc comment for
  // the full precedence ladder. 'override' is a reserved-but-currently-
  // UNPRODUCED value: admin cell overrides (matchCardV2.ts's
  // handleCellOverride) today only feed PLAYER_CARD/standings derivation via
  // shared/applyOverrides.ts, NOT this display pipeline — nothing in this
  // file or its callers ever sets 'override' yet. It exists purely so a
  // future wire-up of admin overrides into this consensus pipeline has an
  // agreed slot to land in — applyUploaderStripPrecedence already treats it
  // as outranking the strip (never overwrites an 'override' cell), so that
  // future work is additive, not a re-litigation of this precedence order.
  source?: 'server' | 'client' | 'strip' | 'override';
  flags?: string[];
}

// Normalise OCR'd player strings for fuzzy comparison: lowercase + strip
// punctuation, brackets, clan tags. Keeps just letters and digits.
//
// `s` is typed `string` everywhere it's called from, but OCR output and
// roster-display arrays sourced from the server are only *typed* non-null —
// at runtime a name the OCR pipeline never resolved comes through as a
// genuine `null`. Coalesce defensively so a null name normalises to '' (and
// gets dropped by callers' `if (!rn) continue` checks) instead of crashing
// the whole render tree on `null.toLowerCase()`.
//
// 2026-07-14 (Lisa's live clan-tag-snap bug fix, backend mirror — see
// src/shared/nameNormalise.ts for the backend twin): strips `(...)` and
// `{...}` bracket-family groups too, not just `[...]` — a clan tag can show
// up wrapped in any of the three punctuation families.
export function normaliseName(s: string): string {
  return (s ?? '').toLowerCase()
    .replace(/\[[^\]]*\]|\([^)]*\)|\{[^}]*\}/g, '')
    .replace(/[^a-z0-9]/g, '');
}

// Strip diacritics/accents to their base Latin letter — "deSá" -> "deSa".
// Mirrors backend's nameNormalise.foldDiacritics (see clanTagLadder test
// header for the pairing). Built from char codes rather than a directly-typed
// combining range, same ASCII-source-safety reasoning as the backend twin.
const COMBINING_DIACRITICS_RE = new RegExp(
  '[' + String.fromCharCode(0x0300) + '-' + String.fromCharCode(0x036f) + ']', 'g',
);
export function foldDiacritics(s: string): string {
  return (s ?? '').normalize('NFD').replace(COMBINING_DIACRITICS_RE, '');
}

// Sørensen-Dice coefficient over bigrams. Small, dependency-free, robust to
// the kind of mid-string typos OCR produces ("d_rose" vs "djd_rose").
export function dice(a: string, b: string): number {
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;
  const grams = (s: string) => {
    const m = new Map<string, number>();
    for (let i = 0; i < s.length - 1; i++) {
      const g = s.slice(i, i + 2);
      m.set(g, (m.get(g) ?? 0) + 1);
    }
    return m;
  };
  const ag = grams(a), bg = grams(b);
  let inter = 0;
  for (const [g, n] of ag) {
    const m = bg.get(g);
    if (m) inter += Math.min(n, m);
  }
  return (2 * inter) / (a.length + b.length - 2);
}

export type SnapMatchKind = 'exact' | 'manual' | 'substring' | 'dice' | 'discriminator-strip' | 'bracket-misread' | 'suffix-rescue' | 'none';
export interface SnapToRosterResult {
  snap: string | null;
  score: number;
  bestCandidate: string | null;
  matched: SnapMatchKind;
}

// Snap an OCR'd player name to the closest roster entry, returning the snap
// + score. Score < 0.55 → caller should drop the row.
//
// This is tier 1 of the clan-tag-snap ladder (2026-07-14) — UNCHANGED from
// before that fix: exact / manual-override / substring / Sørensen-Dice.
// snapToRosterScored (below) wraps this with three additive tiers (the
// discriminator-strip tier added 2026-07-14b, plus the pre-existing
// bracket-misread + suffix-rescue tiers).
function snapToRosterScoredBase(
  candidate: string,
  roster: string[],
  manual: Record<string, string>,
): SnapToRosterResult {
  const c = normaliseName(candidate);
  if (!c) return { snap: null, score: 0, bestCandidate: null, matched: 'none' };

  const manualHit = manual[c] ?? manual[(candidate ?? '').trim().toLowerCase()];
  if (manualHit && roster.includes(manualHit)) {
    return { snap: manualHit, score: 1, bestCandidate: manualHit, matched: 'manual' };
  }

  let best: { name: string; score: number; matched: 'exact' | 'substring' | 'dice' } | null = null;
  for (const rawR of roster) {
    // A roster/display-name entry can itself be a null OCR-derived name
    // (e.g. clan1Display/clan2Display carrying a slot the pipeline never
    // resolved) — coalesce before use so this never throws.
    const r = rawR ?? '';
    const rn = normaliseName(r);
    if (!rn) continue;
    let score: number;
    let matched: 'exact' | 'substring' | 'dice';
    if (rn === c) { score = 1; matched = 'exact'; }
    // Tighten (Lisa, 2026-07-14): substring only counts when the two lengths
    // are comparable (ratio > 0.5) — the backend's snapName has always had
    // this gate; without it "Ryne" matched inside "IXJRyne99" (a stranger).
    // Below the ratio the pair falls through to Dice, which scores honest.
    else if ((rn.includes(c) || c.includes(rn))
      && Math.min(c.length, rn.length) / Math.max(c.length, rn.length) > 0.5) {
      score = 0.85 + 0.15 * (Math.min(c.length, rn.length) / Math.max(c.length, rn.length));
      matched = 'substring';
    } else {
      score = dice(c, rn);
      matched = 'dice';
    }
    if (!best || score > best.score) best = { name: r, score, matched };
  }
  if (!best) return { snap: null, score: 0, bestCandidate: null, matched: 'none' };
  return {
    snap: best.score >= 0.55 ? best.name : null,
    score: best.score,
    bestCandidate: best.name,
    matched: best.score >= 0.55 ? best.matched : 'none',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Discriminator-strip exact match (2026-07-14b, Lisa — closes the #292
// one-direction gap documented in consensus.stripOwnerMismatch.test.ts).
// Mirrors backend's src/shared/nameNormalise.ts findNormalisedExactMatch, as
// wired into nameSnapper.ts's snapNameWithTagNormalise — tier 2 there, run
// unconditionally (no bracket-evidence gate needed) right after the base
// exact/substring/Levenshtein tier, BEFORE any bracket/suffix heuristics.
//
// An Activision-style gamertag carries a trailing `#digits` discriminator
// ("zoey#7966163"). When the discriminator is long relative to the bare
// name, tier 1's substring rule fails its length-ratio gate (0.5 cutoff) and
// its Dice score also falls under the 0.55 commit threshold — a short name
// gets swamped by a long numeric tail. The backend closes this with an
// unconditional strip-then-exact-match tier; this is that tier's frontend
// mirror. Deliberately EXACT-only (never loosens into fuzzy/substring) — a
// stripped remainder that isn't a byte-for-byte (post-normaliseName) roster
// member stays unmatched, falling through to the bracket-misread/suffix-
// rescue tiers exactly as it did before this tier existed.
// ─────────────────────────────────────────────────────────────────────────────

// Trailing #digits discriminator, with an optional dangling '-'/'_' before
// it — byte-identical shape to backend's nameNormalise.ts HASH_SUFFIX_RE.
const TRAILING_DISCRIMINATOR_RE = /[-_]?#\d+\s*$/;

function stripTrailingDiscriminator(s: string): string {
  return (s ?? '').trim().replace(TRAILING_DISCRIMINATOR_RE, '');
}

function findDiscriminatorStripExactMatch(candidate: string, roster: string[]): string | null {
  const target = normaliseName(stripTrailingDiscriminator(candidate));
  if (!target) return null;
  for (const rawR of roster) {
    const r = rawR ?? '';
    if (normaliseName(r) === target) return r;
  }
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Misread-bracket heuristic (2026-07-14 live bug, Lisa) — mirrors backend's
// nameNormalise.stripMisreadBracketPrefix (see rosterMatch.clanTagLadder.test.ts
// header for the pairing / drift-detection convention). OCR sometimes misreads
// the bracket PUNCTUATION itself as letters ("[GN ZA]adrn" -> "IGN_ZAJadrn"),
// so no bracket character survives for normaliseName's bracket-strip to catch.
// Only tried when the raw string starts with "I" (case-insensitive) and a "J"
// appears within roughly the first 10 characters.
// ─────────────────────────────────────────────────────────────────────────────
const MISREAD_BRACKET_SCAN_CHARS = 10;

function stripMisreadBracketPrefix(reading: string): string[] {
  const raw = reading ?? '';
  if (!raw || raw[0].toLowerCase() !== 'i') return [];
  const scanEnd = Math.min(raw.length, MISREAD_BRACKET_SCAN_CHARS + 1);
  const candidates = new Set<string>();
  for (let idx = 1; idx < scanEnd; idx++) {
    if (raw[idx].toLowerCase() !== 'j') continue;
    let rest = raw.slice(idx + 1);
    rest = rest.replace(/^[-_\s]+/, '').replace(/[-_\s]+$/, '');
    if (rest) candidates.add(rest);
  }
  return [...candidates];
}

// ─────────────────────────────────────────────────────────────────────────────
// Suffix rescue (brief step 4) — mirrors backend's
// nameNormalise.findSuffixRescueCandidates + pickLongestUniqueSuffixCandidate.
// Generic last-resort fallback: is a roster name (casefolded, diacritic-
// folded, whitespace-stripped) the TAIL of this reading, within
// edit-distance 1, minimum 4 characters? Prefers the LONGEST qualifying
// roster name; refuses to pick when two or more DISTINCT roster names tie at
// that longest length (ambiguous, stays unresolved).
// ─────────────────────────────────────────────────────────────────────────────
const SUFFIX_RESCUE_MIN_LEN = 4;

function suffixFold(s: string): string {
  return foldDiacritics((s ?? '').toLowerCase()).replace(/\s+/g, '');
}

// Standard (non-OCR-weighted) Levenshtein — deliberately plain, same
// reasoning as backend's nameNormalise.ts twin: this tier's leniency is a
// separate, narrower budget than the Dice-coefficient fuzz used elsewhere in
// this file.
function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp: number[][] = [];
  for (let i = 0; i <= m; i++) { dp[i] = new Array(n + 1).fill(0); dp[i][0] = i; }
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[m][n];
}

interface SuffixRescueCandidate { rosterName: string; distance: number }

function findSuffixRescueCandidates(reading: string, roster: string[]): SuffixRescueCandidate[] {
  const foldedReading = suffixFold(reading);
  if (!foldedReading) return [];
  const out: SuffixRescueCandidate[] = [];
  for (const rawR of roster) {
    const r = rawR ?? '';
    const candidate = suffixFold(r);
    if (candidate.length < SUFFIX_RESCUE_MIN_LEN) continue;
    if (foldedReading.length < candidate.length - 1) continue;
    let best: number | null = null;
    const L = candidate.length;
    for (const winLen of [L - 1, L, L + 1]) {
      if (winLen < 1 || winLen > foldedReading.length) continue;
      const window = foldedReading.slice(-winLen);
      const dist = levenshtein(window, candidate);
      if (best === null || dist < best) best = dist;
      if (best === 0) break;
    }
    if (best !== null && best <= 1) out.push({ rosterName: r, distance: best });
  }
  return out;
}

function pickLongestUniqueSuffixCandidate(candidates: SuffixRescueCandidate[]): SuffixRescueCandidate | null {
  if (candidates.length === 0) return null;
  const byName = new Map<string, SuffixRescueCandidate>();
  for (const c of candidates) {
    const existing = byName.get(c.rosterName);
    if (!existing || c.distance < existing.distance) byName.set(c.rosterName, c);
  }
  const deduped = [...byName.values()];
  const maxLen = Math.max(...deduped.map(c => normaliseName(c.rosterName).length));
  const atMax = deduped.filter(c => normaliseName(c.rosterName).length === maxLen);
  if (atMax.length !== 1) return null;
  return atMax[0];
}

// Snap an OCR'd player name to the closest roster entry — the FULL
// clan-tag-snap ladder (2026-07-14, extended 2026-07-14b). Tier 1
// (snapToRosterScoredBase, above) is tried first and wins untouched if it
// already resolves — zero behaviour change for every case the existing
// exact/manual/substring/Dice logic already handled. Only when tier 1 comes
// up empty do the three additive tiers run, in order: the discriminator-
// strip exact match (2026-07-14b — mirrors backend's tier-2 placement in
// snapNameWithTagNormalise), then the misread-bracket heuristic, then
// suffix-rescue. See src/shared/nameSnapper.ts's snapNameFull for the
// backend twin of this same ladder (mirrored, not shared — see
// rosterMatch.clanTagLadder.test.ts / consensus.clanTagLadder.test.ts for the
// drift-detection test-vector pairing).
export function snapToRosterScored(
  candidate: string,
  roster: string[],
  manual: Record<string, string>,
): SnapToRosterResult {
  const base = snapToRosterScoredBase(candidate, roster, manual);
  if (base.matched !== 'none') return base;

  const discriminatorMatch = findDiscriminatorStripExactMatch(candidate, roster);
  if (discriminatorMatch) {
    return { snap: discriminatorMatch, score: 1, bestCandidate: discriminatorMatch, matched: 'discriminator-strip' };
  }

  const bracketCandidates = stripMisreadBracketPrefix(candidate);
  if (bracketCandidates.length > 0) {
    const resolved = new Map<string, SnapToRosterResult>();
    for (const cand of bracketCandidates) {
      const s = snapToRosterScoredBase(cand, roster, manual);
      if (s.matched === 'none') continue;
      // Tighten (Lisa, 2026-07-14): a STRIPPED remainder may only commit to a
      // roster name within edit distance 1 — the base chain's substring rule
      // is not enough evidence on a shortened string ("IXJRyne99" -> "Ryne99"
      // contains "Ryne" but is distance 2: a stranger, stays unmatched).
      // Mirrors isTightRemainderMatch in src/shared/nameSnapper.ts.
      if (levenshtein(suffixFold(cand), suffixFold(s.snap as string)) > 1) continue;
      const existing = resolved.get(s.snap as string);
      if (!existing || s.score > existing.score) resolved.set(s.snap as string, s);
    }
    if (resolved.size === 1) {
      const [[, winner]] = resolved;
      return { ...winner, matched: 'bracket-misread' };
    }
  }

  const suffixWinner = pickLongestUniqueSuffixCandidate(findSuffixRescueCandidates(candidate, roster));
  if (suffixWinner) {
    return {
      snap: suffixWinner.rosterName,
      score: suffixWinner.distance === 0 ? 1 : 0.7,
      bestCandidate: suffixWinner.rosterName,
      matched: 'suffix-rescue',
    };
  }

  return base; // stays none
}

// ─────────────────────────────────────────────────────────────────────────────
// isStripOwnerMismatch — Lisa, 2026-07-14 (live anti-cheat bug). Detects when
// a shot's OWN uploader strip (uploaderStats.gamertag) belongs to a
// DIFFERENT roster player than the slot it was uploaded into — the real
// repro: zoey's screenshot landed in Punjabi Pan's slot and vice-versa, and
// because of #287's K/D-from-strip policy below, the strip is the FACT
// source for K/D — a swapped strip silently attributed the wrong player's
// kills/deaths as fact, unflagged, and doubles as a cheat vector (upload a
// teammate's better-K/D screenshot into your own slot).
//
// Deliberately narrow — only CONFIDENT when:
//   - the strip gamertag is present/readable, AND
//   - it snaps to a KNOWN roster player (absent/unreadable, or a stranger
//     that snaps to nobody, is left to the existing LOW_CONFIDENCE/
//     UNEXPECTED_NAME machinery instead — never guessed here), AND
//   - that player is DIFFERENT from the slot owner's own roster identity (an
//     OCR-mangled reading of the OWNER'S OWN name that still snaps back to
//     the owner is NOT a mismatch — just noisy OCR on a correct upload).
//
// Pure, side-effect-free — mirrors (does NOT share; see this repo's
// duplication idiom, no cross-layer imports between frontend and backend)
// src/shared/stripOwnerMismatch.ts's isStripOwnerMismatch, adapted to THIS
// layer's own snapToRosterScored fuzzy-matcher (+ manual override rules)
// instead of the backend's snapNameFull (+ knownTags). Both sides are proven
// against an IDENTICAL test-vector table — see
// consensus.stripOwnerMismatch.test.ts (this file) <->
// stripOwnerMismatch.test.ts (backend) for the drift-detection pairing, same
// convention as consensus.clanTagLadder.test.ts <->
// rosterMatch.clanTagLadder.test.ts (#290).
// ─────────────────────────────────────────────────────────────────────────────

export interface StripOwnerMismatchResult {
  /** True only when we're CONFIDENT the strip belongs to someone else. */
  mismatch: boolean;
  /** Roster name the strip's own gamertag snapped to, or null (absent/unreadable/stranger). */
  stripPlayer: string | null;
  /** Roster name the SLOT OWNER resolves to, or null (no owner identity to compare against). */
  slotOwnerPlayer: string | null;
}

function normaliseForStripCompare(s: string): string {
  return s.toLowerCase().trim();
}

/**
 * @param stripGamertag     the shot's own uploader strip reading (raw, unsnapped) — uploaderStats.gamertag.
 * @param slotOwnerGamertag the SLOT owner's own canonical/resolved identity — typically playerIdToDisplay.get(slot.playerId).
 * @param roster            the full match roster to snap against.
 * @param manual            admin manual name-snap overrides (same shape snapToRosterScored already takes).
 */
export function isStripOwnerMismatch(
  stripGamertag: string | undefined,
  slotOwnerGamertag: string | undefined,
  roster: string[],
  manual: Record<string, string> = {},
): StripOwnerMismatchResult {
  const stripTag = (stripGamertag ?? '').trim();
  if (!stripTag) return { mismatch: false, stripPlayer: null, slotOwnerPlayer: null };

  const stripSnap = snapToRosterScored(stripTag, roster, manual);
  if (!stripSnap.snap) {
    // Strip resolves to nobody on the roster — a stranger, not (provably) a
    // mismatch against THIS slot's owner. Leave to the existing
    // LOW_CONFIDENCE/UNEXPECTED_NAME machinery instead of guessing.
    return { mismatch: false, stripPlayer: null, slotOwnerPlayer: null };
  }
  const stripPlayer = stripSnap.snap;

  const ownerTag = (slotOwnerGamertag ?? '').trim();
  if (!ownerTag) return { mismatch: false, stripPlayer, slotOwnerPlayer: null };

  const ownerSnap = snapToRosterScored(ownerTag, roster, manual);
  const slotOwnerPlayer = ownerSnap.snap ?? ownerTag;

  const mismatch = normaliseForStripCompare(stripPlayer) !== normaliseForStripCompare(slotOwnerPlayer);
  return { mismatch, stripPlayer, slotOwnerPlayer };
}

export function aggregateConsensus(
  slots: SubmissionSlotRow[],
  roster: string[],
  manualRules: Record<string, string> = {},
  playerIdToDisplay: Map<string, string> = new Map(),
): {
  perGame: Record<number, ConsensusCell[]>;
  players: Record<number, string[]>;
  extractedNames: Record<string, NameVote[]>;
  drops: Record<number, DropDiagnostic[]>;
} {
  const perGame: Record<number, ConsensusCell[]> = {};
  const players: Record<number, Set<string>> = {};
  const drops: Record<number, DropDiagnostic[]> = {};
  const extractedNamesMap: Record<string, Map<string, number>> = {};

  const ROW_KEY: Record<string, string> = {
    score: 'score', objScore: 'objectiveScore', kills: 'eliminations',
    deaths: 'deaths', time: 'time', plants: 'plants', defuses: 'defuses',
    overloads: 'overloads', captures: 'captures',
  };
  const FIELDS = ['score', 'objScore', 'kills', 'deaths', 'time', 'plants', 'defuses', 'overloads', 'captures'];

  const readRowValue = (row: Record<string, unknown>, key: string): number | undefined => {
    const top = row[key];
    if (typeof top === 'number') return top;
    if (typeof top === 'string') { const n = Number(top); if (!isNaN(n)) return n; }
    const raw = (row.rawValues as Record<string, unknown> | undefined)?.[key];
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'string') { const n = Number(raw); if (!isNaN(n)) return n; }
    return undefined;
  };

  const pushVote = (g: number, snapped: string, field: string, v: number, weight: number) => {
    let cell = perGame[g].find(c => c.player === snapped && c.field === field);
    if (!cell) {
      cell = { player: snapped, field, consensus: undefined, agreement: 0, reads: 0 };
      perGame[g].push(cell);
    }
    const sideMap = (cell as unknown as { __votes?: Map<number, CellVote> });
    if (!sideMap.__votes) sideMap.__votes = new Map();
    const e = sideMap.__votes.get(v);
    if (e) { e.weight += weight; e.count++; }
    else sideMap.__votes.set(v, { value: v, weight, count: 1 });
  };

  for (const slot of slots) {
    const excluded = (slot as unknown as { excludedFromConsensus?: boolean }).excludedFromConsensus;
    if (excluded) continue;
    const conf = (slot as unknown as { ocrConfidence?: number }).ocrConfidence ?? 0.5;
    const weight = conf * conf;
    const g = slot.gameNum;
    if (typeof g !== 'number') continue;
    if (!players[g]) players[g] = new Set();
    if (!perGame[g]) perGame[g] = [];
    if (!drops[g]) drops[g] = [];

    const parsed = (slot as unknown as { parsedScoreboard?: { teams?: Array<{ rows: Array<Record<string, unknown>> }> } }).parsedScoreboard;
    if (parsed?.teams) {
      for (const team of parsed.teams) {
        for (const row of team.rows) {
          const raw = (row as { player?: string }).player?.trim();
          if (!raw) continue;
          const result = snapToRosterScored(raw, roster, manualRules);
          if (!result.snap) {
            drops[g].push({ raw, bestCandidate: result.bestCandidate, score: result.score, gameNum: g });
            continue;
          }
          const snapped = result.snap;
          players[g].add(snapped);

          if (!extractedNamesMap[snapped]) extractedNamesMap[snapped] = new Map();
          const nm = extractedNamesMap[snapped];
          nm.set(raw, (nm.get(raw) ?? 0) + 1);

          for (const field of FIELDS) {
            const v = readRowValue(row, ROW_KEY[field]);
            if (typeof v !== 'number') continue;
            pushVote(g, snapped, field, v, weight);
          }
        }
      }
    }

    // Lisa, 2026-07-13 — uploaderStats is NO LONGER folded into the weighted
    // vote pool here. It used to feed kills/deaths/objScore/extras as just
    // one more (high-weight, since it's a first-party read) vote alongside
    // the cross-shot table rows — which (a) let a personal-strip Objective/
    // Time reading quietly outvote the table's cross-shot consensus, exactly
    // the anti-cheat surface Lisa's policy says must stay 100% table-vote,
    // and (b) still lost outright to a well-agreed WRONG table vote often
    // enough to explain the "scoreboard shows dashes/wrong K-D despite the
    // uploader's own strip having it" reports. K/D now comes from
    // uploaderStats via applyUploaderStripPrecedence below — a precedence
    // override applied AFTER table consensus (client + server merge) is
    // fully resolved, never a competing vote. Score/objScore/time/extras
    // NEVER read from uploaderStats anywhere, full stop — only `gamertag` is
    // still consulted here, to attribute the shot to a roster player (so
    // they show up on the board even on a game where the table never
    // mentioned them) and to track which raw gamertag variant the strip OCR
    // saw (extractedNames, unrelated to any cell value).
    const uploader = (slot as unknown as { uploaderStats?: { gamertag?: string } }).uploaderStats;
    if (uploader) {
      const uploaderTag = uploader.gamertag?.trim();
      let snapped: string | undefined = playerIdToDisplay.get(slot.playerId);
      if (!snapped && uploaderTag) {
        const result = snapToRosterScored(uploaderTag, roster, manualRules);
        if (result.snap) snapped = result.snap;
      }

      if (snapped) {
        players[g].add(snapped);
        // Strip-owner mismatch guard (Lisa, 2026-07-14) — this block never
        // attributes a stat VALUE (see the comment above this uploaderStats
        // block), but it DOES attribute the raw gamertag TEXT as a "seen
        // variant" of `snapped`'s name — and `snapped` is resolved from the
        // SLOT OWNER (playerIdToDisplay), not from what the strip itself
        // says. When the strip belongs to a DIFFERENT known roster player
        // (the live zoey/Punjabi Pan swap), recording ITS raw text under the
        // WRONG player's name is exactly the misattribution this whole fix
        // exists to stop — skip it. `players[g].add(snapped)` above is left
        // untouched: which players get a board ROW is driven by slot
        // ownership, not by the strip's content, so that stays harmless.
        if (uploaderTag && !isStripOwnerMismatch(uploaderTag, snapped, roster, manualRules).mismatch) {
          if (!extractedNamesMap[snapped]) extractedNamesMap[snapped] = new Map();
          const nm = extractedNamesMap[snapped];
          nm.set(uploaderTag, (nm.get(uploaderTag) ?? 0) + 1);
        }
      }
    }
  }

  for (const cells of Object.values(perGame)) {
    if (!Array.isArray(cells)) continue;
    for (const cell of cells) {
      const votes = (cell as unknown as { __votes?: Map<number, CellVote> }).__votes;
      if (!votes) continue;
      const all = [...votes.values()];
      const total = all.reduce((a, b) => a + b.weight, 0);
      const top = all.sort((a, b) => b.weight - a.weight)[0];
      cell.consensus = top.value;
      cell.agreement = total > 0 ? top.weight / total : 0;
      cell.reads = all.reduce((a, b) => a + b.count, 0);
    }
  }

  const rosterOrder = new Map(roster.map((r, i) => [r, i]));

  const extractedNames: Record<string, NameVote[]> = {};
  for (const [snap, m] of Object.entries(extractedNamesMap)) {
    extractedNames[snap] = [...m.entries()]
      .map(([raw, count]) => ({ raw, count }))
      .sort((a, b) => b.count - a.count);
  }

  return {
    perGame,
    players: Object.fromEntries(Object.entries(players).map(([g, s]) => [
      Number(g),
      [...s].sort((a, b) => (rosterOrder.get(a) ?? 999) - (rosterOrder.get(b) ?? 999)),
    ])),
    extractedNames,
    drops,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// mergeServerConsensus — Wave 6, MatchCardLive only.
//
// Once a match's OCR digestion has run at least once, ocrBatchRunner stashes
// its own quality-weighted, Stage-2-corrected consensus on the match row
// itself (match.ocrBatchSummary.consensus — see src/shared/ocrPipelineV2.ts's
// CellConsensus / PipelineResult). That run is the SAME pipeline this file's
// aggregateConsensus() is a client-side approximation of, except the server
// version also benefits from Stage 2 deterministic-slicer re-reads on
// disputed cells. Per Luke's product decision: the server's numbers are
// canonical whenever they exist. This function overlays them, per cell, on
// top of aggregateConsensus()'s output — cells the server has an opinion on
// get replaced; anything it doesn't cover (digestion never ran, or only
// covers a subset of games/players) keeps its client-recomputed value
// untouched. So the caller always gets a fully-populated board; only the
// SOURCE of each individual cell varies.
//
// This does not compute a new consensus — it's a display-time overlay of
// already-canonical server values, keyed by matching row.playerKey (raw
// OCR'd name, lowercased/trimmed — see ServerCellConsensusRow) back onto a
// roster display name via the same snapToRosterScored() this file already
// uses for its own client-side aggregation, so both paths resolve names the
// same way.
export function mergeServerConsensus(
  perGame: Record<number, ConsensusCell[]>,
  players: Record<number, string[]>,
  serverConsensus: ServerCellConsensusRow[] | undefined,
  roster: string[],
  manualRules: Record<string, string> = {},
): { perGame: Record<number, ConsensusCell[]>; players: Record<number, string[]> } {
  const outPerGame: Record<number, ConsensusCell[]> = {};
  for (const [g, cells] of Object.entries(perGame)) {
    outPerGame[Number(g)] = cells.map(c => ({ ...c, source: (c.source ?? 'client') as 'client' }));
  }
  if (!serverConsensus || serverConsensus.length === 0) {
    return { perGame: outPerGame, players };
  }

  const rosterOrder = new Map(roster.map((r, i) => [r, i]));
  const outPlayers: Record<number, string[]> = Object.fromEntries(
    Object.entries(players).map(([g, ps]) => [Number(g), [...ps]]),
  );

  for (const row of serverConsensus) {
    // row.playerKey is typed `string` (ServerCellConsensusRow) but is the raw
    // OCR'd name straight off the server's ocrBatchSummary — a scoreboard
    // row the OCR never resolved a name for comes through as a genuine
    // `null` at runtime. snapToRosterScored/normaliseName are null-safe, but
    // skip explicitly (mirrors aggregateConsensus's `if (!raw) continue`)
    // rather than waste a match attempt on an empty candidate.
    if (!row.playerKey) continue;
    const snap = snapToRosterScored(row.playerKey, roster, manualRules);
    const player = snap.snap;
    // Server read a name that doesn't map to this match's roster (e.g. a
    // scoreboard row it couldn't confidently snap either) — skip rather than
    // guess or invent a new player row.
    if (!player) continue;

    const flags: string[] = [];
    if (row.value === undefined) flags.push('NO_READINGS');
    else if (row.agreement === 1) flags.push('AGREED');
    else if (row.agreement >= 0.67) flags.push('MAJORITY');
    else if (row.agreement >= 0.5) flags.push('TIE');
    else flags.push('ALL_DIFFERENT');
    if (row.source === 'stage2') flags.push('STAGE2_CORRECTED');

    const merged: ConsensusCell = {
      player, field: row.field,
      consensus: row.value,
      agreement: row.agreement,
      reads: row.totalReadings,
      source: 'server',
      flags,
    };

    if (!outPerGame[row.gameNum]) outPerGame[row.gameNum] = [];
    const idx = outPerGame[row.gameNum].findIndex(c => c.player === player && c.field === row.field);
    if (idx >= 0) outPerGame[row.gameNum][idx] = merged;
    else outPerGame[row.gameNum].push(merged);

    // The server may know about a player this match's client-side extraction
    // never surfaced a row for at all (e.g. every shot for their cells was
    // excluded client-side but the server ran before that exclusion, or the
    // client dropped the name-snap on a shot the server later Stage-2
    // corrected) — make sure they still show up in the roster-ordered list.
    if (!outPlayers[row.gameNum]) outPlayers[row.gameNum] = [];
    if (!outPlayers[row.gameNum].includes(player)) {
      outPlayers[row.gameNum] = [...outPlayers[row.gameNum], player]
        .sort((a, b) => (rosterOrder.get(a) ?? 999) - (rosterOrder.get(b) ?? 999));
    }
  }

  return { perGame: outPerGame, players: outPlayers };
}

// ─────────────────────────────────────────────────────────────────────────────
// applyUploaderStripPrecedence — Lisa's policy, 2026-07-13.
//
// Score/Objective/Time cells stay 100% cross-shot table-vote consensus —
// that cross-check across independently-uploaded screenshots of the SAME
// scoreboard is the anti-cheat surface, unchanged by anything below. K/D are
// different: they're PERSONAL stats, and every player's own upload already
// carries them on its bottom strip (uploaderStats — lifted from
// parsedScoreboard.selectedPlayer by OcrBatchRunner). A live example: slot
// SLOT#G1#Pghost-kare-zoey on a real match had uploaderStats K 47 / D 31,
// while ZERO of that game's 8 shots' parsed TABLES carried a K/D reading for
// zoey's row at all — table-vote-only consensus has nothing to average, so
// the cell stayed a permanent dash despite the true number sitting in plain
// text on the player's own screenshot.
//
// Precedence per K/D cell:
//   1. admin cell override — always outranks everything (see the `source:
//      'override'` doc comment on ConsensusCell above for exactly why this
//      function can name that tier but can't yet WIRE it: nothing upstream
//      of this pipeline produces an 'override'-sourced cell today). This
//      function is written to slot strictly UNDER that tier regardless —
//      see the `existing?.source === 'override'` guard below — so wiring an
//      override signal into this pipeline later is additive, not a redesign.
//   2. the player's OWN slot's uploaderStats value for this field, when present
//   3. table-vote consensus (aggregateConsensus + mergeServerConsensus,
//      completely untouched by this function) — "as today" — when no strip
//      reading exists
//   4. dash
//
// Call this LAST, after mergeServerConsensus — the server's table-only
// consensus (ocrBatchSummary.consensus, from ocrPipelineV2.ts's
// consensusFor) unconditionally overwrites a matching client cell whenever
// it has ANY opinion (see mergeServerConsensus above), so a strip resolution
// applied BEFORE that merge would just get clobbered by a plain table vote
// the moment digestion has run — exactly the "two layers disagree" failure
// mode this whole policy exists to prevent.
//
// Safety net: when BOTH a strip value and a table-consensus value exist and
// DISAGREE, the strip value still wins (it's the photographer's own
// scoreboard, not a vote to be outnumbered) but the cell is stamped
// STRIP_TABLE_MISMATCH so an admin can spot it — a pipeline-level quality
// flag, deliberately NOT added to shared/types.ts's admin-settable
// ShotQualityFlag enum (that enum is a distinct, human-set vocabulary; this
// one is machine-derived display sugar on this file's own ConsensusCell,
// same footing as MAJORITY/TIE/ALL_DIFFERENT/STAGE2_CORRECTED above).
// A strip-sourced cell's flags are REPLACED with ['STRIP'] (+ the mismatch
// flag when applicable), never appended to whatever vote-quality flag the
// table consensus had — MAJORITY/AGREED/TIE describe how a NUMBER WAS
// VOTED; once the strip has taken over the cell, none of that describes
// this number's provenance any more, so keeping it around would mislead an
// admin into thinking the DISPLAYED value is what the table agreed on.
//
// Touches 'kills'/'deaths' (fixed uploaderStats keys) PLUS 'objectiveKills'/
// 'captures' (2026-07-14, Lisa — Hardpoint objective columns): the personal
// bottom-strip's trailing extras[] carries "Objective Kills"/"Captures" on a
// Hardpoint screenshot (scoreboardAnalysis.ts's parseTrailingStatLine — see
// STRIP_EXTRA_LABEL_FIELD below), same trust model as K/D — a personal strip
// stat, never a cross-shot table vote, and gated behind the SAME
// isStripOwnerMismatch identity guard below (one guard check, before the
// per-field loop, covers every field this function writes — no per-field
// bypass is possible). aggregateConsensus still never reads
// uploaderStats.objectiveScore/extras into any vote pool (see its own doc
// comment) — this function is the ONLY place extras[] values ever reach a
// board cell, and only for the two fields STRIP_EXTRA_LABEL_FIELD knows
// about ('Time' and any other extra label stay inert, same as before).
// ─────────────────────────────────────────────────────────────────────────────

// 'objScore' added 2026-07-26 (Luke's ruling). The strip carries the player's
// objective score at a top-level key, and it is a materially BETTER reading of
// it than the table is: the strip is large, high-contrast, single-player text
// while the table is small multi-row text, and the two are independent
// observations of the same truth. Live evidence (MATCH#1785083826620) —
// strip vs table across all eight players: 1580/1588, 1010/1810, 1045/1845,
// 810/818, 1395/1385, and three exact agreements. Diana's 1580 is confirmed
// ground truth, so the strip is right and the table is wrong; every
// disagreement is a 0 read as an 8 (one 9-as-8). The table over-reads 8s.
//
// Why voting could not save this: all N shots photograph the SAME digits, so
// the errors are CORRELATED — the wrong value wins the majority AND reports
// high agreement, which is the very signal Stage 2 uses to pick re-reads.
// Backend twin: shared/ocrPipelineV2.ts's applyStripPrecedence, which now
// applies the same rule to the canonical stored consensus.
type StripCellField = 'kills' | 'deaths' | 'objectiveKills' | 'captures' | 'objScore';

// Which uploaderStats key backs each strippable cell field whose value lives
// at a fixed top-level key. 'objectiveKills'/'captures' are NOT here — they
// live in the strip's variable-length extras[] array instead (see
// STRIP_EXTRA_LABEL_FIELD).
const STRIP_UPLOADER_FIELD: Partial<Record<StripCellField, 'eliminations' | 'deaths' | 'objectiveScore'>> = {
  kills: 'eliminations',
  deaths: 'deaths',
  objScore: 'objectiveScore',
};

// Case-insensitive label matchers for the two Hardpoint-only strip extras.
// Scouted against scoreboardAnalysis.ts's parseTrailingStatLine, which emits
// the label exactly as OCR'd off the strip after whitespace-collapse (e.g.
// "Objective Kills", "Captures" — see Diana's real strip in the tapestry: "7
// Objective Kills / 14 Captures"). Unanchored on purpose (matches the brief's
// own patterns) — no other strip label realistically contains "capture" or
// "objective ... kills" as a substring, and the primary-field "OBJ. SCORE"
// label is filtered out of extras[] entirely upstream (parseTrailingStatLine
// drops anything matching PRIMARY_DETAIL_LABELS), so there's no collision
// with objectiveScore.
const STRIP_EXTRA_LABEL_FIELD: Array<{ field: StripCellField; pattern: RegExp }> = [
  { field: 'objectiveKills', pattern: /objective\s*kills?/i },
  { field: 'captures', pattern: /captures?/i },
];

const ALL_STRIP_FIELDS: StripCellField[] = ['kills', 'deaths', 'objectiveKills', 'captures', 'objScore'];

// Resolve one strippable field's value off a slot's uploaderStats — either a
// fixed top-level key (kills/deaths) or a label match inside extras[]
// (objectiveKills/captures). Returns undefined when the strip never carried
// that stat at all (e.g. a non-Hardpoint game's strip has no "Objective
// Kills"/"Captures" line — mode-awareness falls out of this returning
// undefined, not a hardcoded per-mode gate).
function stripFieldValue(
  uploader: { eliminations?: number; deaths?: number; objectiveScore?: number; extras?: ParsedSelectedPlayerExtraStatLike[] },
  field: StripCellField,
): number | undefined {
  const directKey = STRIP_UPLOADER_FIELD[field];
  if (directKey) {
    const v = uploader[directKey];
    return typeof v === 'number' ? v : undefined;
  }
  const matcher = STRIP_EXTRA_LABEL_FIELD.find(entry => entry.field === field);
  if (!matcher) return undefined;
  const hit = (uploader.extras ?? []).find(e => typeof e?.label === 'string' && matcher.pattern.test(e.label));
  return typeof hit?.value === 'number' ? hit.value : undefined;
}

// Minimal local shape for a strip extra — mirrors (does not import; frontend
// never imports backend shared types, same duplication idiom as the rest of
// this file) scoreboardAnalysis.ts's ParsedSelectedPlayerExtraStat.
interface ParsedSelectedPlayerExtraStatLike { label: string; value: number }

export function applyUploaderStripPrecedence(
  perGame: Record<number, ConsensusCell[]>,
  players: Record<number, string[]>,
  slots: SubmissionSlotRow[],
  roster: string[],
  manualRules: Record<string, string> = {},
  playerIdToDisplay: Map<string, string> = new Map(),
): { perGame: Record<number, ConsensusCell[]>; players: Record<number, string[]> } {
  const outPerGame: Record<number, ConsensusCell[]> = {};
  for (const [g, cells] of Object.entries(perGame)) outPerGame[Number(g)] = cells.map(c => ({ ...c }));
  const outPlayers: Record<number, string[]> = Object.fromEntries(
    Object.entries(players).map(([g, ps]) => [Number(g), [...ps]]),
  );
  const rosterOrder = new Map(roster.map((r, i) => [r, i]));

  for (const slot of slots) {
    // Same admin-exclusion gate aggregateConsensus's own slot loop applies —
    // a shot an admin flagged out of consensus shouldn't have its personal
    // strip trusted either, for the identical reason its table votes aren't.
    const excluded = (slot as unknown as { excludedFromConsensus?: boolean }).excludedFromConsensus;
    if (excluded) continue;
    const g = slot.gameNum;
    if (typeof g !== 'number') continue;

    const uploader = (slot as unknown as {
      uploaderStats?: {
        gamertag?: string; eliminations?: number; deaths?: number;
        extras?: ParsedSelectedPlayerExtraStatLike[];
      };
    }).uploaderStats;
    if (!uploader) continue;

    // Same identity resolution as aggregateConsensus's own uploaderStats
    // block: prefer the slot-identity map (robust against the strip OCR
    // mangling its own uploader's gamertag), fall back to fuzzy-snapping the
    // strip's own gamertag reading.
    const uploaderTag = uploader.gamertag?.trim();
    let snapped: string | undefined = playerIdToDisplay.get(slot.playerId);
    if (!snapped && uploaderTag) {
      const result = snapToRosterScored(uploaderTag, roster, manualRules);
      if (result.snap) snapped = result.snap;
    }
    if (!snapped) continue;

    if (!outPerGame[g]) outPerGame[g] = [];
    if (!outPlayers[g]) outPlayers[g] = [];
    if (!outPlayers[g].includes(snapped)) {
      outPlayers[g] = [...outPlayers[g], snapped]
        .sort((a, b) => (rosterOrder.get(a) ?? 999) - (rosterOrder.get(b) ?? 999));
    }

    // Strip-owner mismatch guard (Lisa, 2026-07-14, live anti-cheat bug) —
    // `snapped` above is the SLOT OWNER's identity (playerIdToDisplay), never
    // actually compared against what the strip itself claims — that gap is
    // exactly how zoey's and Punjabi Pan's screenshots got silently swapped
    // (MATCH#1783931157772 G1): each player's real K/D landed on the OTHER
    // player's cell, badged 'STRIP' (presented as fact). When the strip
    // CONFIDENTLY belongs to a different known roster player than this slot
    // owner, refuse to write ANY of its K/D onto `snapped`'s cells — fall
    // back to whatever table-vote consensus already produced (or a dash),
    // never attribute a stranger's numbers as fact. The player still gets
    // a board row above (driven by slot ownership, not by the strip), and a
    // CORRECT upload (strip matches the owner, including an OCR-mangled
    // reading of their OWN name) is completely unaffected — see
    // isStripOwnerMismatch's doc comment for the exact confidence rules.
    if (isStripOwnerMismatch(uploaderTag, snapped, roster, manualRules).mismatch) continue;

    for (const field of ALL_STRIP_FIELDS) {
      const stripValue = stripFieldValue(uploader, field);
      if (typeof stripValue !== 'number') continue;

      const idx = outPerGame[g].findIndex(c => c.player === snapped && c.field === field);
      const existing = idx >= 0 ? outPerGame[g][idx] : undefined;
      // Tier 1 — admin override (not producible yet, see doc comment above;
      // this guard is what makes wiring it in later purely additive).
      if (existing?.source === 'override') continue;

      const tableValue = existing?.consensus;
      // MAGNITUDE SANITY (2026-07-26). The strip outranks the table because it
      // is bigger, cleaner text — but a strip whose OWN digits were mangled
      // (500 read as 9999) is the worse source, and blindly preferring it
      // would replace a defensible table vote with nonsense. An order-of-
      // magnitude gap is the tell. Mirrors Stage 2's existing
      // `sanity-magnitude` rule (shared/ocrPipelineV2.ts) and the backend twin
      // applyStripPrecedence, rather than inventing a third notion of "wildly
      // wrong". This guard is what lets objScore join the strip fields at all:
      // it preserves the property the blanket field-ban used to enforce (a
      // rogue strip can never clobber the table) while still letting the
      // realistic case through — Diana's strip 1580 vs table 1588.
      if (
        typeof tableValue === 'number' && tableValue > 0
        && (stripValue > tableValue * 5 || stripValue < tableValue / 5)
      ) continue;
      const flags: string[] = ['STRIP'];
      if (tableValue !== undefined && tableValue !== stripValue) flags.push('STRIP_TABLE_MISMATCH');

      const merged: ConsensusCell = {
        player: snapped, field,
        consensus: stripValue,
        agreement: 1,
        reads: 1,
        source: 'strip',
        flags,
      };
      if (idx >= 0) outPerGame[g][idx] = merged;
      else outPerGame[g].push(merged);
    }
  }

  return { perGame: outPerGame, players: outPlayers };
}
